import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from 'recharts';
import colors from '../../../config/colors';
import Typography from '../../../components/shared/Typography/Typography.component';
import { i18nService } from '../../../services/i18n.service';
import { HomePageWeeklyHoursDataResponse } from '../HomePage.container';
import moment from 'moment';

const minutesToHHMM = (totalMinutes: number): string => {
	const h = Math.floor(totalMinutes / 60);
	const m = Math.round(totalMinutes % 60);
	return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const CustomXTick = ({ x, y, payload }: any) => (
	<text
		x={x}
		y={y}
		dy={14}
		textAnchor='middle'
		fontSize={12}
		fill={colors.primaryDarkBlue}
		fontFamily='RubikMedium'
	>
		{i18nService.translate(payload.value)}
	</text>
);

const CustomYTick = ({ x, y, payload }: any) => (
	<text
		x={x}
		y={y}
		textAnchor='end'
		fontSize={12}
		fill={colors.primaryDarkBlue}
		fontFamily='RubikMedium'
	>
		{payload.value}
	</text>
);

const CustomTooltip = ({ active, payload }: any) => {
	if (active && payload && payload.length) {
		const { date, workLogs, dayName } = payload[0].payload;
		return (
			<div
				style={{
					backgroundColor: '#fff',
					borderRadius: 6,
					padding: '12px',
					boxShadow: '2px 4px 8px 0 rgba(0,0,0,0.16)',
					display: 'flex', 
					flexDirection: 'column',
					gap: 4,
					minWidth: 120,
					direction: 'rtl',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							backgroundColor: colors.datepickerChooseBG,
							display: 'inline-block',
							flexShrink: 0,
						}}
					/>
					<span
						style={{
							fontFamily: 'RubikRegular',
							fontSize: 14,
							fontWeight: 400,
							color: colors.primaryDarkBlue,
						}}
					>
						{minutesToHHMM(workLogs ?? 0)} שעות
					</span>
				</div>
				<span
					style={{
						fontFamily: 'RubikRegular',
						fontSize: 12,
						color: colors.tooltipPercentage,
						//paddingRight: 18,
					}}
				>
					{`יום ${dayName} ${moment(date).format('DD/MM')}`}
				</span>
			</div>
		);
	}
	return null;
};

const HEBREW_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const DailyHoursChart = ({ weeklyData }: { weeklyData: HomePageWeeklyHoursDataResponse[] | null }) => {

	const chartData = useMemo(() => weeklyData
	? weeklyData.map(d => ({
		...d,
		dayName: HEBREW_DAYS[moment(d.date).day()],
		workLogsHours: (d.workLogs ?? 0) / 60,
	}))
	: [], [weeklyData]);

	return (
		<View style={styles.card}>
			<Typography
				size={14}
				font='medium'
				color='primaryDarkBlue'
				style={styles.title}
			>
				{i18nService.translate('chart.dailyHours')}
			</Typography>
			<div style={{ width: '100%', height: 200 }}>
				<ResponsiveContainer width='100%' height='100%'>
					<BarChart
						data={chartData}
						margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
					>
						<CartesianGrid
							vertical={false}
							horizontal={false}
							stroke={colors.divider}
						/>
						<XAxis
							dataKey='dayName'
							tickLine={false}
							axisLine={true}
							tick={<CustomXTick />}
						/>
						<YAxis
							tickLine={true}
							axisLine={true}
							tick={<CustomYTick />}
							tickCount={3}
							domain={[0, 12]}
							ticks={[0, 5, 10]}
						/>
						<Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
						<Bar dataKey='workLogsHours' stackId='a' fill={colors.titlePrimary} radius={[4, 4, 0, 0]} background={{ fill: colors.subMenuBG }} barSize={20} />
					</BarChart>
				</ResponsiveContainer>
			</div>
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		flex: 1,
		backgroundColor: colors.white,
		borderRadius: 8,
		padding: 24,
		gap: 16,
	},
	title: {
		textAlign: 'right',
	},
});

export default DailyHoursChart;
